import type { ProposedAction } from "../../api/types";

export type GroupIcon =
  | "create"
  | "update"
  | "delete"
  | "status"
  | "document"
  | "other";

export interface ActionGroup {
  id: string;
  label: string;
  icon: GroupIcon;
  actions: ProposedAction[];
  isReadOnly: boolean;
}

const QUERY_TOOLS = new Set([
  "search_work_items",
  "get_work_item",
  "get_board_state",
  "get_project_metadata",
  "analyze_codebase",
  "search_documents",
]);

export function isQueryTool(toolName: string): boolean {
  return QUERY_TOOLS.has(toolName);
}

function groupId(actions: ProposedAction[]): string {
  return actions.map((a) => a.id).join("-");
}

function itemLabel(action: ProposedAction): string {
  const params = action.user_modification || action.parameters;
  const title = params.title ?? action.before_snapshot?.title;
  if (title) return `"${String(title)}"`;
  return "item";
}

export function groupActions(actions: ProposedAction[]): ActionGroup[] {
  const groups: ActionGroup[] = [];
  const used = new Set<string>();

  // Build a lookup from work_item_id -> all actions targeting it
  const byItemId = new Map<string, ProposedAction[]>();
  for (const action of actions) {
    if (isQueryTool(action.tool_name)) continue;
    const params = action.user_modification || action.parameters;
    const itemId = String(params.work_item_id ?? params.id ?? "");
    if (!itemId) continue;
    if (!byItemId.has(itemId)) byItemId.set(itemId, []);
    const itemList = byItemId.get(itemId);
    if (itemList) itemList.push(action);
  }

  // Rule 1: Create + children cluster
  for (let i = 0; i < actions.length; i++) {
    const action = actions[i];
    if (used.has(action.id)) continue;
    if (!["create_work_item", "create_child_item"].includes(action.tool_name))
      continue;

    const params = action.user_modification || action.parameters;
    const cluster: ProposedAction[] = [action];

    // Look ahead for set_parent or create_child_item targeting same parent
    for (let j = i + 1; j < actions.length; j++) {
      const next = actions[j];
      if (used.has(next.id)) continue;
      const nextParams = next.user_modification || next.parameters;
      if (
        next.tool_name === "set_parent" ||
        next.tool_name === "create_child_item"
      ) {
        const nextParentId = String(nextParams.parent_id ?? "");
        const actionItemId = String(
          params.work_item_id ?? params.id ?? action.id,
        );
        if (
          nextParentId === actionItemId ||
          nextParams.parent_id === params.work_item_id
        ) {
          cluster.push(next);
        }
      }
    }

    if (cluster.length > 1) {
      cluster.forEach((a) => used.add(a.id));
      groups.push({
        id: groupId(cluster),
        label: `Create ${itemLabel(action)} with children`,
        icon: "create",
        actions: cluster,
        isReadOnly: false,
      });
    }
  }

  // Rule 3: Status sweep (3+ same-status actions before grouping by item)
  const statusActions = actions.filter(
    (a) => !used.has(a.id) && a.tool_name === "change_work_item_status",
  );
  const byStatusId = new Map<string, ProposedAction[]>();
  for (const action of statusActions) {
    const params = action.user_modification || action.parameters;
    const statusId = String(params.status_id ?? "");
    if (!statusId) continue;
    if (!byStatusId.has(statusId)) byStatusId.set(statusId, []);
    const statusList = byStatusId.get(statusId);
    if (statusList) statusList.push(action);
  }
  for (const [, sweepActions] of byStatusId) {
    if (sweepActions.length >= 3) {
      sweepActions.forEach((a) => used.add(a.id));
      const params =
        sweepActions[0].user_modification || sweepActions[0].parameters;
      const statusName = String(params.status_name ?? "new status");
      groups.push({
        id: groupId(sweepActions),
        label: `Move ${sweepActions.length} items to ${statusName}`,
        icon: "status",
        actions: sweepActions,
        isReadOnly: false,
      });
    }
  }

  // Rule 2: Same target item grouping
  for (const [, itemActions] of byItemId) {
    const ungrouped = itemActions.filter((a) => !used.has(a.id));
    if (ungrouped.length < 2) continue;
    ungrouped.forEach((a) => used.add(a.id));
    groups.push({
      id: groupId(ungrouped),
      label: `Update ${itemLabel(ungrouped[0])}`,
      icon: "update",
      actions: ungrouped,
      isReadOnly: false,
    });
  }

  // Rule 4: Document operations
  const docActions = actions.filter(
    (a) =>
      !used.has(a.id) &&
      ["create_document", "publish_document"].includes(a.tool_name),
  );
  const docByTitle = new Map<string, ProposedAction[]>();
  for (const action of docActions) {
    const params = action.user_modification || action.parameters;
    const title = String(params.title ?? "");
    if (!title) continue;
    if (!docByTitle.has(title)) docByTitle.set(title, []);
    const docList = docByTitle.get(title);
    if (docList) docList.push(action);
  }
  for (const [title, docGroup] of docByTitle) {
    if (
      docGroup.some((a) => a.tool_name === "create_document") &&
      docGroup.some((a) => a.tool_name === "publish_document")
    ) {
      docGroup.forEach((a) => used.add(a.id));
      groups.push({
        id: groupId(docGroup),
        label: `Publish new document "${title}"`,
        icon: "document",
        actions: docGroup,
        isReadOnly: false,
      });
    }
  }

  // Remaining mutation actions — each becomes its own single-action group
  for (const action of actions) {
    if (used.has(action.id)) continue;
    const isReadOnly = isQueryTool(action.tool_name);
    let label: string;
    let icon: GroupIcon;

    switch (action.tool_name) {
      case "create_work_item":
      case "create_child_item":
        label = `Create ${itemLabel(action)}`;
        icon = "create";
        break;
      case "delete_work_item":
        label = `Delete ${itemLabel(action)}`;
        icon = "delete";
        break;
      case "change_work_item_status":
        label = `Change status of ${itemLabel(action)}`;
        icon = "status";
        break;
      case "create_document":
        label = `Create document ${itemLabel(action)}`;
        icon = "document";
        break;
      case "update_document":
      case "publish_document":
      case "archive_document":
        label = `${action.tool_name.replace(/_/g, " ")} ${itemLabel(action)}`;
        icon = "document";
        break;
      default:
        label = action.tool_name
          .split("_")
          .map((w) => w[0].toUpperCase() + w.slice(1))
          .join(" ");
        icon = isReadOnly ? "other" : "update";
    }

    groups.push({
      id: action.id,
      label,
      icon,
      actions: [action],
      isReadOnly,
    });
  }

  return groups;
}
